import React from 'react'
import { Button, Form, Input, Modal, Select, Table, Upload } from 'antd'
import type { TableColumnsType } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    buildPaginatedListPagination,
    PAGINATED_LIST_TABLE_SIZE,
} from '../../../components/common/PaginatedListTable'
import type { ModelVersionSummary } from '../../../types/modelManagement'
import type {
    BindingFormValues,
    RawClassBindingOption,
    RemarkFormValues,
    UploadFormValues,
} from './pageConfig'
import {
    MODEL_CONFIGURATION_DETAIL_MODAL_WIDTH,
    MODEL_MANAGEMENT_I18N_PREFIX,
    VERSION_DETAIL_TABLE_SCROLL_X,
} from './pageConfig'

interface UploadModalProps {
    form: ReturnType<typeof Form.useForm<UploadFormValues>>[0]
    onSubmit: () => void
    onClose: () => void
    open: boolean
    selectedFile: File | null
    setSelectedFile: (file: File | null) => void
    uploading: boolean
}

interface BindingModalProps {
    activeClassOptions: RawClassBindingOption[]
    activeManualTypeOptions: Array<{ label: string; value: string }>
    form: ReturnType<typeof Form.useForm<BindingFormValues>>[0]
    onClose: () => void
    onSubmit: () => void
    open: boolean
    saving: boolean
}

interface RemarkModalProps {
    form: ReturnType<typeof Form.useForm<RemarkFormValues>>[0]
    onClose: () => void
    onSubmit: () => void
    open: boolean
    saving: boolean
}

interface DetailModalProps {
    columns: TableColumnsType<ModelVersionSummary>
    modelLabel: string
    onClose: () => void
    open: boolean
    versions: ModelVersionSummary[]
}

export const ModelUploadModal: React.FC<UploadModalProps> = ({
    form,
    onClose,
    onSubmit,
    open,
    selectedFile,
    setSelectedFile,
    uploading,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <Modal
            title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.uploadModal.title`)}
            open={open}
            confirmLoading={uploading}
            onCancel={onClose}
            onOk={onSubmit}
            okText={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitUpload`)}
            maskClosable={false}
            destroyOnClose
            forceRender
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.form.modelPackage`)}
                >
                    <Upload
                        accept=".zip"
                        maxCount={1}
                        beforeUpload={(file) => {
                            setSelectedFile(file)
                            return false
                        }}
                        onRemove={() => {
                            setSelectedFile(null)
                            return true
                        }}
                        fileList={
                            selectedFile
                                ? [
                                      {
                                          uid: selectedFile.name,
                                          name: selectedFile.name,
                                          status: 'done',
                                      },
                                  ]
                                : []
                        }
                    >
                        <Button icon={<UploadOutlined />}>
                            {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.selectPackage`)}
                        </Button>
                    </Upload>
                </Form.Item>
                <Form.Item
                    label={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.form.remark`)}
                    name="remark"
                >
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        maxLength={1000}
                        placeholder={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.remarkPlaceholder`
                        )}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export const ModelRemarkModal: React.FC<RemarkModalProps> = ({
    form,
    onClose,
    onSubmit,
    open,
    saving,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <Modal
            title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.remarkModal.title`)}
            open={open}
            confirmLoading={saving}
            onCancel={onClose}
            onOk={onSubmit}
            okText={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitRemark`)}
            maskClosable={false}
            destroyOnClose
            forceRender
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.form.remark`)}
                    name="remark"
                >
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        maxLength={1000}
                        placeholder={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.remarkPlaceholder`
                        )}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export const ModelBindingModal: React.FC<BindingModalProps> = ({
    activeClassOptions,
    activeManualTypeOptions,
    form,
    onClose,
    onSubmit,
    open,
    saving,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <Modal
            title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.bindingModal.title`)}
            open={open}
            confirmLoading={saving}
            onCancel={onClose}
            onOk={onSubmit}
            okText={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitBinding`)}
            maskClosable={false}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.form.boundRawClasses`)}
                    name="boundRawClasses"
                >
                    <Select
                        allowClear
                        getPopupContainer={(triggerNode) =>
                            triggerNode.closest<HTMLElement>('.ant-modal-content') ??
                            document.body
                        }
                        mode="multiple"
                        options={activeClassOptions.map((option) => ({
                            disabled: option.disabled,
                            label: option.label,
                            value: option.value,
                        }))}
                        popupMatchSelectWidth={false}
                        placeholder={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.boundRawClassesPlaceholder`
                        )}
                        showSearch
                    />
                </Form.Item>
                <Form.Item
                    label={t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.form.boundManualTypes`
                    )}
                    name="boundManualTypes"
                >
                    <Select
                        allowClear
                        getPopupContainer={(triggerNode) =>
                            triggerNode.closest<HTMLElement>('.ant-modal-content') ??
                            document.body
                        }
                        mode="multiple"
                        options={activeManualTypeOptions}
                        placeholder={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.boundManualTypesPlaceholder`
                        )}
                        showSearch
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export const ModelDetailModal: React.FC<DetailModalProps> = ({
    columns,
    modelLabel,
    onClose,
    open,
    versions,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <Modal
            title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.configurationModal.title`, {
                model: modelLabel,
            })}
            open={open}
            onCancel={onClose}
            footer={null}
            maskClosable={false}
            width={MODEL_CONFIGURATION_DETAIL_MODAL_WIDTH}
            destroyOnClose
        >
            <Table
                rowKey="id"
                dataSource={versions}
                columns={columns}
                size={PAGINATED_LIST_TABLE_SIZE}
                pagination={buildPaginatedListPagination({
                    defaultPageSize: 10,
                    showTotal: (total) =>
                        t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                            total,
                        }),
                })}
                scroll={{ x: VERSION_DETAIL_TABLE_SCROLL_X }}
            />
        </Modal>
    )
}
